import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const ExposureChart = () => {
  const [timeframe, setTimeframe] = useState('1D');
  const [selectedPairs, setSelectedPairs] = useState(['EURUSD', 'GBPUSD', 'USDJPY']);

  // Mock exposure data over time
  const exposureData = [
    { time: '09:00', EURUSD: 45000, GBPUSD: 32000, USDJPY: 28000, AUDUSD: 15000, USDCAD: 12000 },
    { time: '10:00', EURUSD: 48000, GBPUSD: 35000, USDJPY: 25000, AUDUSD: 18000, USDCAD: 14000 },
    { time: '11:00', EURUSD: 52000, GBPUSD: 38000, USDJPY: 30000, AUDUSD: 20000, USDCAD: 16000 },
    { time: '12:00', EURUSD: 49000, GBPUSD: 42000, USDJPY: 35000, AUDUSD: 22000, USDCAD: 18000 },
    { time: '13:00', EURUSD: 55000, GBPUSD: 45000, USDJPY: 32000, AUDUSD: 25000, USDCAD: 20000 },
    { time: '14:00', EURUSD: 58000, GBPUSD: 48000, USDJPY: 38000, AUDUSD: 28000, USDCAD: 22000 },
    { time: '15:00', EURUSD: 62000, GBPUSD: 52000, USDJPY: 42000, AUDUSD: 30000, USDCAD: 25000 },
    { time: '16:00', EURUSD: 59000, GBPUSD: 49000, USDJPY: 45000, AUDUSD: 32000, USDCAD: 28000 }
  ];

  const timeframes = ['1H', '4H', '1D', '1W', '1M'];
  const currencyPairs = ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD'];
  
  const pairColors = {
    EURUSD: '#3B82F6',
    GBPUSD: '#10B981',
    USDJPY: '#F59E0B',
    AUDUSD: '#8B5CF6',
    USDCAD: '#EF4444'
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{`Time: ${label}`}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry?.color }}
                />
                <span className="text-sm text-muted-foreground">{entry?.dataKey}</span>
              </div>
              <span className="text-sm font-mono text-foreground">
                {formatCurrency(entry?.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const togglePairSelection = (pair) => {
    setSelectedPairs(prev => 
      prev?.includes(pair) 
        ? prev?.filter(p => p !== pair)
        : [...prev, pair]
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="BarChart3" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Exposure by Currency Pairs</h2>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Timeframe Selector */}
          <div className="flex bg-muted rounded-lg p-1">
            {timeframes?.map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors duration-200 ${
                  timeframe === tf
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
          
          {/* Export Button */}
          <button className="flex items-center space-x-1 px-3 py-2 text-sm bg-muted hover:bg-muted/80 rounded-md transition-colors duration-200">
            <Icon name="Download" size={16} />
            <span>Export</span>
          </button>
        </div>
      </div>
      {/* Currency Pair Toggles */}
      <div className="flex flex-wrap gap-2 mb-6">
        {currencyPairs?.map((pair) => (
          <button
            key={pair}
            onClick={() => togglePairSelection(pair)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              selectedPairs?.includes(pair)
                ? 'bg-primary/10 text-primary border border-primary/20' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: pairColors?.[pair] }}
            />
            <span>{pair}</span>
          </button>
        ))}
      </div>
      {/* Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={exposureData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              {selectedPairs?.map((pair) => (
                <linearGradient key={pair} id={`gradient-${pair}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={pairColors?.[pair]} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={pairColors?.[pair]} stopOpacity={0.05}/>
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="time" 
              className="text-xs text-muted-foreground"
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              className="text-xs text-muted-foreground"
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            {selectedPairs?.map((pair) => (
              <Area
                key={pair}
                type="monotone"
                dataKey={pair}
                stroke={pairColors?.[pair]}
                strokeWidth={2}
                fill={`url(#gradient-${pair})`}
                connectNulls
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Chart Summary */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        {selectedPairs?.slice(0, 4)?.map((pair) => {
          const latestData = exposureData?.[exposureData?.length - 1];
          const previousData = exposureData?.[exposureData?.length - 2];
          const change = latestData?.[pair] - previousData?.[pair];
          const changePercent = ((change / previousData?.[pair]) * 100)?.toFixed(1);
          
          return (
            <div key={pair} className="text-center">
              <div className="text-xs text-muted-foreground mb-1">{pair}</div>
              <div className="text-sm font-mono font-medium text-foreground">
                {formatCurrency(latestData?.[pair])}
              </div>
              <div className={`text-xs flex items-center justify-center space-x-1 ${
                change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                <Icon name={change >= 0 ? 'TrendingUp' : 'TrendingDown'} size={12} />
                <span>{changePercent}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ExposureChart;